import React, { useCallback, useMemo } from 'react';

import { NetworkStatus } from '@apollo/client';
import { TransactionHistoryItemType } from '@ocx-app/modules/transaction-history/transaction-history.types';
import { useIntl } from 'react-intl';
import { PointsStrategy } from '@ocx/feat-loyalty-currency';
import { TotalPointsEarnedSection, useTotalPointsEarned } from '@ocx/feat-membership-lifetime-metrics';
import { SmConstrainedPageBody } from '@ocx/ui';
import { useConfiguration } from '../../modules/configuration/useConfiguration';
import { useGetNavigationActions } from '../../hooks/useGetNavigationActions';
import { useSanitizedLoyaltyActionHistory } from '../../modules/transaction-history/loyalty-action-history.hooks';
import { PAGINATION_SIZE } from '../../modules/transaction-history/transaction-history.constants';
import {
  mapActivateTransactionToTransactionHistoryItem,
  mapPointsChangeToTransactionHistoryItem,
} from '../../modules/transaction/utils';
import { createHistoryListSegments } from '../../modules/transaction-history/transaction-history.utils';
import { TransactionsListController } from './transactions-list.controller';

export const LoyaltyActionsListController: React.FC = () => {
  const intl = useIntl();
  const { pushToActivateTransactionReceiptNested, pushToActivatePointsChangeReceiptNested } = useGetNavigationActions();
  const pointsStrategy = useConfiguration('loyaltyCurrency.pointsStrategy');
  const totalPointsEarnedEnabled = useConfiguration('totalPointsEarned.enabled');
  const {
    totalPointsEarned,
    networkStatus: totalPointsEarnedNetworkStatus,
    refetch: totalPointsEarnedRefetch,
  } = useTotalPointsEarned({
    skip: !totalPointsEarnedEnabled,
  });
  const { data, fetchMore, refetch, networkStatus } = useSanitizedLoyaltyActionHistory({
    variables: {
      size: PAGINATION_SIZE,
      transactionCursor: null,
      pointChangeCursor: null,
      skipPointChanges: pointsStrategy === PointsStrategy.NotUsed,
    },
  });

  const segments = useMemo(() => {
    return createHistoryListSegments([
      ...data.pointChanges.map((edge) => mapPointsChangeToTransactionHistoryItem(edge.node, intl)),
      ...data.transactions.map((edge) => mapActivateTransactionToTransactionHistoryItem(edge.node, intl)),
    ]);
  }, [data, intl]);

  const refetchLoyaltyAndTotalPoints = useCallback(async () => {
    const tasks: Promise<unknown>[] = [refetch()];
    if (totalPointsEarnedEnabled) {
      tasks.push(totalPointsEarnedRefetch());
    }
    await Promise.all(tasks);
  }, [refetch, totalPointsEarnedRefetch, totalPointsEarnedEnabled]);

  return (
    <>
      {totalPointsEarnedEnabled && totalPointsEarnedNetworkStatus !== NetworkStatus.error && (
        <SmConstrainedPageBody>
          <TotalPointsEarnedSection
            pointsEarned={totalPointsEarned}
            loading={totalPointsEarnedNetworkStatus < NetworkStatus.ready}
          />
        </SmConstrainedPageBody>
      )}
      <TransactionsListController
        networkStatus={networkStatus}
        segments={segments}
        refetch={refetchLoyaltyAndTotalPoints}
        fetchMore={fetchMore}
        onListItemClick={(id, type) =>
          [
            TransactionHistoryItemType.Transaction,
            TransactionHistoryItemType.OrderAhead,
            TransactionHistoryItemType.Cancelled,
          ].includes(type)
            ? pushToActivateTransactionReceiptNested(id)
            : pushToActivatePointsChangeReceiptNested(id)
        }
      />
    </>
  );
};
